import { Component, DestroyRef, inject, signal, effect, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

import { SessionService } from '../../core/session.service';
import { ForecastGraphComponent } from '../previsions/forecast-graph/forecast-graph.component';
import { ProfileIaComponent } from '../profile-ia/profile-ia.component';
import { AjustementManuelComponent } from '../ajustement-manuel/ajustement-manuel.component';
import { ForecastService, ForecastPoint, ForecastDto } from '../../core/services/forecast.service';
import { TimezoneService } from '../../core/services/timezone.service';
import { formatInTimeZone } from 'date-fns-tz';
import { PageSiteSwitcherComponent } from '../../component/page-site-switcher/page-site-switcher.component';

type QueueSnap = { id: string; name: string };

type SummaryNow = {
  currentIa: number | null;
  baseline: number | null;
  deltaIa: number | null;
  updatedAgoMin: number | null;
};

type SummaryNext = {
  nextHourRange: string;
  nextOverHighAt: string | null;
  peakAt: string | null;
  peakMinutes: number | null;
  optimalArrivalTime: string | null;
  lastManualAdjustment: string | null;
  slotsOverHighCount: number;
};

@Component({
  standalone: true,
  selector: 'app-previsions',
  imports: [CommonModule, FormsModule, ForecastGraphComponent, ProfileIaComponent, AjustementManuelComponent, PageSiteSwitcherComponent, TranslatePipe],
  templateUrl: './previsions.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./previsions.component.scss'],
})
export class PrevisionsComponent implements OnInit {
  @Input() inline = false;
  private session = inject(SessionService);
  private api = inject(ForecastService);
  private timezone = inject(TimezoneService);
  private destroyRef = inject(DestroyRef);

  // seuils (cohérents avec le graph) — configurés dans Qualité·Objectifs (défaut org ou surcharge site)
  get lowTh(): number { return this.session.affluenceThresholds()?.waitLowMinutes ?? 10; }
  get highTh(): number { return this.session.affluenceThresholds()?.waitHighMinutes ?? 20; }

  queues = signal<QueueSnap[]>([]);
  selectedQueueId = signal<string | null>(null);
  selectedQueueIdInternal: string | null = null;

  // résumés affichés
  now = signal<SummaryNow>({ currentIa: null, baseline: null, deltaIa: null, updatedAgoMin: null });
  next = signal<SummaryNext>({ nextHourRange: '', nextOverHighAt: null, peakAt: null, peakMinutes: null, optimalArrivalTime: null, lastManualAdjustment: null, slotsOverHighCount: 0 });
  wizardConfigured = signal<boolean>(false);

  // Données de prévision partagées avec le composant graphique
  sharedForecastData = signal<ForecastDto | null>(null);

  // Gestion sous-page ProfileIA
  showProfileIaSubpage = signal<boolean>(false);

  // Gestion sous-page AjustementManuel
  showAjustementManuelSubpage = signal<boolean>(false);

  // Sélection du jour
  selectedDay = signal<number>(this.timezone.getDateInEstablishmentTimezone().getDay());

  days = [
    { value: 1, labelKey: 'common.days.shortByCode.mon' },
    { value: 2, labelKey: 'common.days.shortByCode.tue' },
    { value: 3, labelKey: 'common.days.shortByCode.wed' },
    { value: 4, labelKey: 'common.days.shortByCode.thu' },
    { value: 5, labelKey: 'common.days.shortByCode.fri' },
    { value: 6, labelKey: 'common.days.shortByCode.sat' },
    { value: 0, labelKey: 'common.days.shortByCode.sun' }
  ];

  selectDay(day: number) {
    this.selectedDay.set(day);
  }

  openIaProfile() {
    this.showProfileIaSubpage.set(true);
  }

  closeProfileIaSubpage() {
    this.showProfileIaSubpage.set(false);
  }

  openManualAdjustment() {
    this.showAjustementManuelSubpage.set(true);
  }

  closeAjustementManuelSubpage() {
    this.showAjustementManuelSubpage.set(false);
    // Réinitialiser la sélection de queue à la première queue disponible
    const queues = this.queues();
    if (queues && queues.length > 0) {
      this.selectedQueueId.set(queues[0].id);
    }
    // Rafraîchir les données après les ajustements manuels
    setTimeout(() => this.refreshSummaries(), 100);
  }

  /** Effet 1 : aligne la sélection quand les queues arrivent */
  private _sync = effect(() => {
    const list = this.session.queues() as QueueSnap[];
    this.queues.set(list ?? []);

    const keep = this.selectedQueueId();
    const exists = !!keep && list?.some(q => q.id === keep);
    const next = exists ? keep : (list && list.length ? list[0].id : null);

    this.selectedQueueId.set(next);
    this.selectedQueueIdInternal = next;
  }, { allowSignalWrites: true });

  /** Effet : recharge résumés sur changement de file, établissement ou jour */
  private _watchChanges = effect(() => {
    const id = this.selectedQueueId();
    const estId = this.session.selectedId();
    this.selectedDay(); // dépendance explicite : re-run sur changement de jour

    this.selectedQueueIdInternal = id;

    if (id && estId) {
      this.refreshSummaries();
    }
  }, { allowSignalWrites: true });

  onQueueChanged(id: string | null) {
    this.selectedQueueId.set(id);
    this.selectedQueueIdInternal = id;
  }

  /** Méthode publique pour forcer le rafraîchissement (appelée après config ORA) */
  forceRefresh() {
    this.forecastCache.clear();
    this.refreshSummaries();
  }

  ngOnInit() {
    // L'effect _watchChanges se chargera du premier appel
  }

  /**
   * Cache court (même durée que le rafraîchissement live ailleurs dans l'app) des réponses
   * déjà récupérées par jour, pour éviter de refaire l'appel réseau quand on revient sur un
   * onglet jour (Lun/Mar/.../Dim) déjà consulté.
   */
  private readonly forecastCache = new Map<string, { response: ForecastDto; fetchedAt: number }>();
  private readonly FORECAST_CACHE_TTL_MS = 60_000;

  private refreshSummaries() {
    const qid = this.selectedQueueId();
    if (!qid) {
      this.now.set({ currentIa: null, baseline: null, deltaIa: null, updatedAgoMin: null });
      this.next.set({ nextHourRange: '', nextOverHighAt: null, peakAt: null, peakMinutes: null, optimalArrivalTime: null, lastManualAdjustment: null, slotsOverHighCount: 0 });
      this.wizardConfigured.set(false);
      return;
    }

    const cacheKey = `${qid}:${this.selectedDay()}`;
    const cached = this.forecastCache.get(cacheKey);
    if (cached && (Date.now() - cached.fetchedAt) < this.FORECAST_CACHE_TTL_MS) {
      this.applyForecast(cached.response);
      return;
    }

    // Seul appel nécessaire : journée complète avec wizard
    this.api.getDay(qid, undefined, this.selectedDay()).subscribe({
      next: (f) => {
        this.forecastCache.set(cacheKey, { response: f, fetchedAt: Date.now() });
        this.applyForecast(f);
      },
      error: (err) => {
        console.error('Erreur lors du chargement des prévisions:', err);
        this.sharedForecastData.set(null);
        this.wizardConfigured.set(false);
        this.now.set({ currentIa: null, baseline: null, deltaIa: null, updatedAgoMin: null });
        this.next.set({ nextHourRange: '', nextOverHighAt: null, peakAt: null, peakMinutes: null, optimalArrivalTime: null, lastManualAdjustment: null, slotsOverHighCount: 0 });
      }
    });
  }

  private applyForecast(f: ForecastDto) {
    // Stocker les données complètes pour le graphique
    this.sharedForecastData.set(f);

    const pts: ForecastPoint[] = f?.points ?? [];
    const afterNow = filterAfterNow(pts, this.timezone);
    const firstHigh = afterNow.find(p => (p.minutes ?? 0) > this.highTh);
    const pmax = maxPoint(pts);
    const pmin = minPoint(pts);

    // Calculer fourchette prochaine heure
    const now = this.timezone.getDateInEstablishmentTimezone();
    const nextHourTime = new Date(now.getTime() + 60 * 60 * 1000);
    const currentHM = `${now.getHours()}:${now.getMinutes().toString().padStart(2, '0')}`;
    const nextHourHM = `${nextHourTime.getHours()}:${nextHourTime.getMinutes().toString().padStart(2, '0')}`;

    const nextHourPts = pts.filter(p => {
      const timeNormalized = normalizeTime(p.time ?? '00:00');
      const currentNormalized = normalizeTime(currentHM);
      const nextHourNormalized = normalizeTime(nextHourHM);
      return timeNormalized >= currentNormalized && timeNormalized <= nextHourNormalized;
    });

    const openNextHourPts = nextHourPts.filter(p => (p.minutes ?? 0) > 0);
    let nextHourRange = '';
    if (openNextHourPts.length > 0) {
      const minutes = openNextHourPts.map(p => p.minutes ?? 0);
      const avg = Math.round(minutes.reduce((a, b) => a + b, 0) / minutes.length);
      nextHourRange = `${avg}`;
    }

    const hasWizard = f.wizard_configured !== undefined ? !!f.wizard_configured : !f.degraded;

    // Formater la date du dernier ajustement manuel
    let lastManualAdjustment: string | null = null;
    if (f.manual_adjustment) {
      const date = new Date(f.manual_adjustment);
      const tz = this.timezone.getEstablishmentTimezone();
      lastManualAdjustment = formatInTimeZone(date, tz, 'dd/MM/yyyy HH:mm');
    }

    // Compter les créneaux > 20 min
    const slotsOverHighCount = pts.filter(p => (p.minutes ?? 0) > this.highTh).length;
    const totalSlots = pts.length;
    const slotsOverHighPercent = totalSlots > 0 ? Math.round((slotsOverHighCount / totalSlots) * 100) : 0;

    this.wizardConfigured.set(hasWizard);
    this.now.set({ currentIa: null, baseline: null, deltaIa: null, updatedAgoMin: null });
    this.next.set({
      nextHourRange: nextHourRange ?? '',
      nextOverHighAt: firstHigh ? (firstHigh.time ?? null) : null,
      peakAt: pmax?.time ?? null,
      peakMinutes: pmax?.minutes != null ? round(pmax.minutes) : null,
      optimalArrivalTime: pmin?.time ?? null,
      lastManualAdjustment,
      slotsOverHighCount: slotsOverHighPercent
    });
  }

  signed(v: number | null): string {
    if (v == null) return '—';
    const s = v > 0 ? '+' : '';
    return `${s}${v}`;
  }
}

/* ================= helpers ================= */

function round(n: number): number { return Math.round((n ?? 0) * 1) / 1; }

function avgMinutes(pts: ForecastPoint[]): number {
  if (!pts?.length) return 0;
  const openPts = pts.filter(p => (p.minutes ?? 0) > 0);
  if (!openPts.length) return 0;
  const sum = openPts.reduce((a, p) => a + (p.minutes ?? 0), 0);
  return sum / openPts.length;
}

/** Baseline à l'heure courante (HH:MM). Fallback = premier créneau de l'heure. */
function baselineAtNow(points: ForecastPoint[], timezone: TimezoneService): number {
  if (!points?.length) return 0;
  const hh = String(timezone.getDateInEstablishmentTimezone().getHours()).padStart(2, '0') + ':';
  const exact = points.find(p => p.time === `${hh}00`);
  if (exact) return exact.minutes ?? 0;
  const sameHour = points.find(p => typeof p.time === 'string' && p.time.startsWith(hh));
  return sameHour?.minutes ?? 0;
}

function filterAfterNow(pts: ForecastPoint[], timezone: TimezoneService): ForecastPoint[] {
  const d = timezone.getDateInEstablishmentTimezone();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const now = `${hh}:${mm}`;
  return pts?.filter(p => {
    const timeNormalized = normalizeTime(p.time ?? '00:00');
    return timeNormalized >= now;
  }) ?? [];
}

function normalizeTime(time: string): string {
  const [h, m] = time.split(':');
  return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
}
function currentHm(): string {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}
function maxPoint(pts: ForecastPoint[]): ForecastPoint | null {
  if (!pts?.length) return null;
  return pts.reduce((best, p) => !best || (p.minutes ?? 0) > (best.minutes ?? 0) ? p : best, null as any);
}
function minPoint(pts: ForecastPoint[]): ForecastPoint | null {
  const open = pts?.filter(p => (p.minutes ?? 0) > 0);
  if (!open?.length) return null;
  return open.reduce((best, p) => !best || (p.minutes ?? 0) < (best.minutes ?? 0) ? p : best, null as any);
}
function minutesAgo(iso: string, timezone: TimezoneService): number | null {
  const t = Date.parse(iso); if (Number.isNaN(t)) return null;
  const nowInTz = timezone.getDateInEstablishmentTimezone().getTime();
  return Math.max(0, Math.round((nowInTz - t)/60000));
}